import { useEffect, useState } from "react";
import { api, fmtMoney, today, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Ship } from "lucide-react";
import AgenciaMexicana from "@/pages/AgenciaMexicana";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const emptyForm = { fecha_operacion: today(), concepto: "", referencia: "", referencia_ampliada: "", cargo: "" };

export default function Aduanas() {
  const { can } = useAuth();
  const canEdit = can("aduanas", "edit");
  const [tab, setTab] = useState("americana");
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [fMes, setFMes] = useState("");
  const [fAnio, setFAnio] = useState("");
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const load = () => {
    const p = new URLSearchParams();
    if (fMes) p.append("mes", fMes);
    if (fAnio) p.append("anio", fAnio);
    api.get(`/aduanas/americana?${p.toString()}`).then((r) => setRows(r.data));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [fMes, fAnio]);

  const total = rows.reduce((s, r) => s + Number(r.cargo || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/aduanas/americana", { ...form, cargo: parseFloat(form.cargo) || 0 });
      toast.success("Movimiento registrado");
      setForm(emptyForm);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/aduanas/americana/${editId}`, { ...editForm, cargo: parseFloat(editForm.cargo) || 0 });
      toast.success("Movimiento actualizado");
      setEditId(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const del = async (id) => { if (!window.confirm("¿Eliminar movimiento?")) return; await api.delete(`/aduanas/americana/${id}`); toast.success("Eliminado"); load(); };

  const input = "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900";

  return (
    <div className="space-y-6">
      <div className="inline-flex bg-slate-100 rounded-md p-1">
        <button data-testid="tab-aduanas-americana" onClick={() => setTab("americana")}
          className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${tab === "americana" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
          Agencia Americana
        </button>
        <button data-testid="tab-aduanas-mexicana" onClick={() => setTab("mexicana")}
          className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${tab === "mexicana" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
          Agencia Mexicana
        </button>
      </div>

      {tab === "americana" ? (
        <>
          {canEdit && (
            <div className="bg-white border border-slate-200 rounded-md shadow-sm">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
                <Ship className="h-4 w-4 text-slate-500" />
                <h3 className="font-heading font-semibold text-slate-900">Captura — Agencia Americana</h3>
              </div>
              <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-6 gap-4">
                <div className="md:col-span-1">
                  <label className="text-xs text-slate-500">Fecha Operación</label>
                  <input data-testid="amer-fecha" type="date" className={input} value={form.fecha_operacion} onChange={(e) => setForm({ ...form, fecha_operacion: e.target.value })} required />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs text-slate-500">Concepto</label>
                  <input data-testid="amer-concepto" className={input} value={form.concepto} onChange={(e) => setForm({ ...form, concepto: e.target.value })} required />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Referencia</label>
                  <input data-testid="amer-referencia" className={input} value={form.referencia} onChange={(e) => setForm({ ...form, referencia: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Ref. Ampliada</label>
                  <input data-testid="amer-ref-ampliada" className={input} value={form.referencia_ampliada} onChange={(e) => setForm({ ...form, referencia_ampliada: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Cargo $</label>
                  <input data-testid="amer-cargo" type="number" step="any" className={input} value={form.cargo} onChange={(e) => setForm({ ...form, cargo: e.target.value })} placeholder="0.00" required />
                </div>
                <div className="md:col-span-6">
                  <button data-testid="amer-submit" className="bg-slate-900 text-white rounded-md px-5 py-2 text-sm font-medium hover:bg-slate-800 flex items-center gap-2">
                    <Plus className="h-4 w-4" /> Agregar movimiento
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
              <h3 className="font-heading font-semibold text-slate-900">Movimientos</h3>
              <div className="flex items-center gap-2 flex-wrap">
                <select data-testid="amer-filtro-mes" value={fMes} onChange={(e) => setFMes(e.target.value)} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900">
                  <option value="">Todos los meses</option>
                  {MESES.map((m, i) => <option key={m} value={String(i + 1)}>{m}</option>)}
                </select>
                <input data-testid="amer-filtro-anio" type="number" placeholder="Año" value={fAnio} onChange={(e) => setFAnio(e.target.value)} className="w-24 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
                <span className="text-sm text-slate-500 ml-2">Total: <span className="font-bold text-slate-900 tabular-nums">{fmtMoney(total)}</span></span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 border-y border-slate-200">
                  <tr>
                    {["Fecha Operación", "Concepto", "Referencia", "Referencia Ampliada"].map((h) => (
                      <th key={h} className="px-4 py-3 font-medium text-left">{h}</th>
                    ))}
                    <th className="px-4 py-3 font-medium text-right">Cargo $</th>
                    {canEdit && <th className="px-4 py-3" />}
                  </tr>
                </thead>
                <tbody data-testid="amer-table">
                  {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Sin movimientos para el periodo</td></tr>}
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="px-4 py-3 text-slate-900">{r.fecha_operacion}</td>
                      <td className="px-4 py-3">{r.concepto}</td>
                      <td className="px-4 py-3 text-slate-600">{r.referencia}</td>
                      <td className="px-4 py-3 text-slate-600">{r.referencia_ampliada}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-medium">{fmtMoney(r.cargo)}</td>
                      {canEdit && (
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button data-testid={`amer-edit-${r.id}`} onClick={() => { setEditId(r.id); setEditForm({ fecha_operacion: r.fecha_operacion, concepto: r.concepto, referencia: r.referencia || "", referencia_ampliada: r.referencia_ampliada || "", cargo: r.cargo }); }} className="text-slate-400 hover:text-slate-900 mr-3"><Pencil className="h-4 w-4" /></button>
                          <button data-testid={`amer-del-${r.id}`} onClick={() => del(r.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {editId && editForm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) setEditId(null); }}>
              <div className="bg-white rounded-md shadow-lg w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="amer-edit-dialog">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="font-heading font-semibold text-slate-900">Editar movimiento</h3>
                  <button onClick={() => setEditId(null)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
                </div>
                <form onSubmit={saveEdit} className="p-6 grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-slate-500">Fecha Operación</label>
                    <input data-testid="amer-edit-fecha" type="date" className={input} value={editForm.fecha_operacion} onChange={(e) => setEditForm({ ...editForm, fecha_operacion: e.target.value })} required />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Cargo $</label>
                    <input data-testid="amer-edit-cargo" type="number" step="any" className={input} value={editForm.cargo} onChange={(e) => setEditForm({ ...editForm, cargo: e.target.value })} required />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-slate-500">Concepto</label>
                    <input data-testid="amer-edit-concepto" className={input} value={editForm.concepto} onChange={(e) => setEditForm({ ...editForm, concepto: e.target.value })} required />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Referencia</label>
                    <input data-testid="amer-edit-referencia" className={input} value={editForm.referencia} onChange={(e) => setEditForm({ ...editForm, referencia: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Ref. Ampliada</label>
                    <input data-testid="amer-edit-ref-ampliada" className={input} value={editForm.referencia_ampliada} onChange={(e) => setEditForm({ ...editForm, referencia_ampliada: e.target.value })} />
                  </div>
                  <div className="col-span-2 flex justify-end gap-2 pt-2">
                    <button type="button" onClick={() => setEditId(null)} className="border border-slate-300 rounded-md px-4 py-2 text-sm hover:bg-slate-50">Cancelar</button>
                    <button data-testid="amer-edit-save" className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-800">Guardar cambios</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </>
      ) : (
        <AgenciaMexicana />
      )}
    </div>
  );
}
